/**
 * English messages. Source of the Messages type — Arabic must provide every key.
 *
 * CONTENT GOVERNANCE: marketing/landing copy here is neutral DRAFT copy that
 * describes platform features only. It contains no legal, distributor,
 * exclusivity, heritage, performance or warranty-term claims. Sections are
 * tracked as 'draft' in src/content/contentStatus.ts until the business
 * approves final copy.
 */
import { plural } from '../plural';

export const en = {
  common: {
    backToSite: 'Back to website',
    stagingRibbon: 'Staging — content and media pending approval',
    brandName: 'Al-Qinaa',
    brandNameAr: 'القناع الشفاف',
    partnerBrand: 'Johnson Window Films',
    tagline: 'Johnson Window Films for your vehicle — chosen, installed and recorded with care.',
    loading: 'Loading…',
    retry: 'Try again',
    save: 'Save',
    saving: 'Saving…',
    saved: 'Saved',
    cancel: 'Cancel',
    continue: 'Continue',
    back: 'Back',
    next: 'Next',
    submit: 'Submit',
    close: 'Close',
    enlarge: 'Enlarge',
    enlargePhoto: 'Enlarge photo: {alt}',
    search: 'Search',
    optional: 'Optional',
    remove: 'Remove',
    edit: 'Edit',
    add: 'Add',
    viewAll: 'View all',
    signIn: 'Sign in',
    signOut: 'Sign out',
    register: 'Create account',
    myAccount: 'My account',
    language: 'Language',
    theme: 'Display mode',
    themeToDark: 'Switch to dark mode',
    themeToLight: 'Switch to light mode',
    skipToContent: 'Skip to main content',
    errorGeneric: 'Something went wrong. Please try again.',
    empty: 'Nothing here yet.',
    comingSoon: 'Coming soon',
    notConfigured: {
      title: 'We can’t load this right now',
      body: 'This service is temporarily unavailable. Please try again shortly.',
    },
    status: {
      sample: 'SAMPLE',
      demo: 'DEMO DATA',
      placeholder: 'PLACEHOLDER',
      integrationReady: 'INTEGRATION READY',
      requiresApi: 'REQUIRES API',
      requiresCredentials: 'REQUIRES CREDENTIALS',
      requiresBusinessConfirmation: 'REQUIRES BUSINESS CONFIRMATION',
      draftContent: 'Draft content — pending approval',
      developmentVisual: 'Development visual',
      prototypeReference: 'PROTOTYPE REFERENCE',
    },
    /** Counted noun phrases (Arabic needs all six plural forms; see src/i18n/plural.ts). */
    counts: {
      branches: plural({ one: '{count} branch', other: '{count} branches' }),
      cities: plural({ one: '{count} city', other: '{count} cities' }),
    },
  },

  nav: {
    home: 'Home',
    vehicles: 'Vehicles',
    services: 'Services',
    centers: 'Centers',
    products: 'Johnson films',
    book: 'Book',
    verify: 'Verify',
    track: 'Track',
    warranty: 'Warranty',
    contact: 'Contact',
    story: 'Our story',
    authenticity: 'Authenticity',
    privacy: 'Privacy policy',
    credits: 'Credits',
    menu: 'Menu',
    closeMenu: 'Close menu',
    primary: 'Primary',
  },

  landing: {
    hero: {
      eyebrow: 'Johnson Window Films',
      title: 'Your vehicle.\nSeen in a new light.',
      subtitle: 'Discover Johnson Window Films for your vehicle, choose the film that suits you, and book your installation — all in one place.',
      primaryCta: 'Choose your vehicle',
      secondaryCta: 'Book a visit',
      scrollHint: 'Scroll to explore',
      skipToVehicle: 'Skip to choose your vehicle',
    },
    stage: {
      label: 'Your journey',
      words: {
        vehicle: 'Vehicle',
        glass: 'Glass',
        light: 'Light',
        protection: 'Protection',
        transformation: 'Transformation',
      },
      lines: {
        vehicle: 'It begins with your vehicle.',
        glass: 'Every journey is seen through glass.',
        light: 'Every day, the sun meets your glass.',
        protection: 'A Johnson film, fitted to your glass.',
        transformation: 'The same vehicle. A new view.',
      },
      choose: 'Choose your vehicle',
      reduceMotion: 'Reduce motion',
      restoreMotion: 'Turn motion back on',
    },
    disclosure: {
      illustrativeEn: 'Illustrative Visualization',
      illustrativeAr: 'تصور توضيحي',
    },
    vehicles: {
      label: 'Your vehicle',
      title: 'Start with what you drive',
      body: 'Choose your make, model and year, and we’ll carry it through your booking.',
      cta: 'Continue to booking',
      chooseCta: 'Choose your vehicle',
      changeCta: 'Change vehicle',
    },
    stickyBook: {
      cta: 'Book a visit',
      noSelection: 'Book your installation in minutes',
      interestCount: '{count} selected',
    },
    services: {
      label: 'Services',
      title: 'Services',
      body: 'Choose a service to see our centers and available times.',
      cta: 'All services',
      empty: 'Our services will be listed here shortly.',
      book: 'Book this service',
      duration: '{minutes} min',
    },
    families: {
      label: 'Johnson Window Films',
      title: 'Product families',
      body: 'Explore the Johnson Window Films range available at Al-Qinaa centers.',
      pendingSpecs: 'Ask our team for full technical details.',
      cta: 'Explore all films',
      empty: 'Our film range will be listed here shortly.',
      interestCta: 'I’m interested',
      interested: 'Added to your visit',
      details: 'Details',
    },
    book: {
      label: 'Book',
      title: 'Book your visit',
      body: 'Pick a service — your vehicle and chosen films are already saved for you.',
      selection: 'Your selection',
      noSelection: 'No vehicle or film chosen yet. You can add them as you book.',
      change: 'Change',
      cta: 'Continue to booking',
    },
    installation: {
      label: 'Installation',
      title: 'Installed at our centers',
      body: 'Bring your vehicle to one of our centers. Our team fits your chosen film and records the installation for you.',
      cta: 'Book a visit',
    },
    verify: {
      label: 'Verification',
      title: 'Check what’s on your glass',
      body: 'Each installation carries a QR label. Scan it, or enter its code, to view your installation record.',
    },
    warranty: {
      label: 'Digital warranty',
      title: 'Your warranty, always with you',
      body: 'Your warranty record is kept in your account, ready whenever you need it — no paper to lose.',
      signedInCta: 'Go to my warranty',
      signedOutCta: 'Sign in to your warranty',
      infoCta: 'How the digital warranty works',
      chain: {
        title: 'From film to your account',
        product: 'Johnson product',
        center: 'Installation center',
        qr: 'QR label',
        record: 'Digital record',
        account: 'Your account',
      },
    },
    why: {
      label: 'Why Al-Qinaa',
      title: 'Everything in one place',
      items: [
        { title: 'Choose with confidence', body: 'See the films and services for your vehicle before you visit.' },
        { title: 'Book in minutes', body: 'Pick a center and a time that suits you, online.' },
        { title: 'Check it anytime', body: 'Every installation has a record you can look up with its QR code.' },
        { title: 'Warranty in your account', body: 'Your warranty record stays with you, not in a glovebox.' },
      ],
    },
    story: {
      label: 'Our story',
      title: 'Al-Qinaa',
      body: 'Al-Qinaa brings Johnson Window Films to drivers with one simple journey — choose your film, book your visit, and keep your installation record and warranty in your account.',
    },
    ctas: {
      booking: { title: 'Book your installation', body: 'Choose a center and a time that suits you.', cta: 'Book now' },
      verify: {
        title: 'Verify a product',
        body: 'Enter the code printed below your QR label.',
        placeholder: 'QR code',
        cta: 'Verify',
      },
      warranty: { title: 'Digital warranty', body: 'Find your warranty record in your account.', cta: 'Open my warranty' },
      tracking: {
        title: 'Track a shipment',
        body: 'Enter your tracking code to see where your shipment is.',
        placeholder: 'Tracking code',
        cta: 'Track',
      },
      order: { title: 'Your bookings', body: 'Sign in to see your upcoming and past visits.', cta: 'My bookings' },
      register: {
        title: 'Create your account',
        body: 'Save your vehicles, manage your bookings and keep your warranty in one place.',
        cta: 'Create account',
      },
    },
    contact: {
      label: 'Support',
      title: 'We’re here to help',
      body: 'Questions about films, bookings or your warranty? Our team will help.',
      cta: 'Contact us',
    },
  },

  footer: {
    rights: '© {year} Al-Qinaa. All rights reserved.',
    explore: 'Explore',
    trust: 'Trust & authenticity',
    account: 'Account',
    support: 'Support',
    label: 'Site links',
    languageLabel: 'Language',
  },

  auth: {
    login: {
      title: 'Sign in',
      subtitle: 'Welcome back. Your vehicles, bookings and warranty are waiting.',
      email: 'Email',
      password: 'Password',
      submit: 'Sign in',
      forgot: 'Forgot password?',
      noAccount: 'New here?',
      createAccount: 'Create an account',
      invalid: 'The email or password is incorrect.',
      unverified: 'Please confirm your email address first — the link is in your inbox.',
    },
    register: {
      title: 'Create your account',
      subtitle: 'Save your vehicles, book faster and keep your warranty in one place.',
      fullName: 'Full name',
      mobile: 'Mobile number',
      mobileCountry: 'Country calling code',
      mobileHint: 'Choose the country, then enter the number. In Saudi Arabia, for example 05XXXXXXXX.',
      email: 'Email',
      password: 'Password',
      passwordHint: 'At least 10 characters with upper- and lower-case letters and a number.',
      confirmPassword: 'Confirm password',
      preferredLanguage: 'Preferred language',
      submit: 'Create account',
      haveAccount: 'Already have an account?',
      vehicleNote: 'You can add your vehicles after creating your account.',
      exists: 'An account with this email may already exist. Try signing in or resetting your password.',
    },
    verifyEmail: {
      title: 'Check your email',
      body: 'We sent a verification link to {email}. Open it to activate your account.',
      bodyGeneric: 'We sent you a verification link. Open it to activate your account.',
      backToLogin: 'Back to sign in',
    },
    forgot: {
      title: 'Reset your password',
      body: 'Enter your email and we will send you a link to set a new password.',
      submit: 'Send reset link',
      sent: 'If an account exists for that email, a reset link is on its way.',
    },
    reset: {
      requiredByAdmin: 'Your administrator asked you to set a new password before you continue.',
      title: 'Set a new password',
      submit: 'Update password',
      done: 'Your password has been updated.',
      invalidLink: 'This reset link is invalid or has expired. Request a new one.',
    },
    callback: {
      working: 'Completing sign-in…',
      failed: 'We could not complete sign-in. The link may have expired.',
    },
    signedOut: 'You have been signed out.',
    resend: 'Resend verification email',
    resent: 'If your account still needs verification, a new link is on its way.',
    rateLimited: 'Too many attempts. Please wait a minute and try again.',
    goToLogin: 'Go to sign in',
    newPassword: 'New password',
    confirmNewPassword: 'Confirm new password',
    checkingLink: 'Checking your link…',
    verifiedSignIn: 'If you opened the link in another browser, your email may already be verified — sign in to continue.',
    reauthenticate: 'For your security, sign out and sign in again, then change your password.',
    samePassword: 'Choose a password that is different from your current one.',
  },

  validation: {
    required: 'This field is required.',
    nameLength: 'Please enter your full name (2–150 characters).',
    email: 'Please enter a valid email address.',
    mobile: 'Please enter a valid mobile number.',
    passwordRules: 'Use at least 10 characters with upper- and lower-case letters and a number.',
    passwordMismatch: 'Passwords do not match.',
    vin: 'A VIN has 17 letters and numbers (no I, O or Q).',
  },

  vehicles: {
    selector: {
      legend: 'Your vehicle',
      make: 'Make',
      model: 'Model',
      year: 'Year',
      trim: 'Trim',
      choose: 'Select…',
      loading: 'Loading…',
      noData: 'We couldn’t find vehicles to show right now.',
      sampleNote: 'Vehicle list contains sample records for demonstration.',
      steps: { make: 'Make', model: 'Model', year: 'Year', trim: 'Trim' },
      chooseMake: 'Choose your make',
      chooseModel: 'Choose your {make} model',
      chooseYear: 'Which year is your {model}?',
      chooseTrim: 'Which trim?',
      skipTrim: 'Not sure — skip',
      searchMakes: 'Search makes',
      searchModels: 'Search models',
      noMatch: 'No matches. Try a different spelling.',
      allTypes: 'All',
      change: 'Change',
      yourVehicle: 'Your vehicle',
      missingVehicle: 'Can’t find your vehicle? Continue anyway — our team will confirm it with you.',
      bodyTypes: {
        sedan: 'Sedan',
        suv: 'SUV',
        coupe: 'Coupe',
        hatchback: 'Hatchback',
        pickup: 'Pickup',
        van: 'Van',
        convertible: 'Convertible',
        wagon: 'Wagon',
        other: 'Vehicle',
      },
    },
    explorer: {
      title: 'Find your vehicle',
      body: 'Choose your vehicle and we’ll keep it with you through booking and installation.',
      selected: 'Selected vehicle',
      compatibilityPending: 'Our team will confirm the right films for your vehicle at your visit.',
      bookCta: 'Book for this vehicle',
    },
    mine: {
      title: 'My vehicles',
      empty: 'You have not added a vehicle yet.',
      add: 'Add a vehicle',
      addTitle: 'Add a vehicle',
      vin: 'VIN',
      plate: 'Plate number',
      color: 'Color',
      nickname: 'Nickname',
      saveVehicle: 'Save vehicle',
      removeConfirm: 'Remove this vehicle?',
      primary: 'Primary',
    },
  },

  catalog: {
    servicesTitle: 'Services',
    servicesBody: 'Everything we offer at our branches — choose a service and book your visit.',
    familiesTitle: 'Johnson Window Films',
    familiesBody: 'Explore the film range available at our centers. Our team will help you choose.',
    specsPending: 'Technical details on request',
    priceOnRequest: 'Price on request',
    from: 'From {price}',
    family: {
      notFoundTitle: 'Family not found',
      notFoundBody: 'We couldn’t find this film. Explore the full range instead.',
      backToFamilies: 'All film families',
      seeOnVehicle: 'See it on your vehicle',
      bookWithThisFilm: 'Book with this film',
      figuresTitle: 'Measured performance',
      figuresNote: 'From the approved specification sheet. Each figure is shown on its own.',
      shadesTitle: 'Available shades',
      specsTitle: 'Specifications',
      glassTitle: 'Glass by glass',
      glassBody: 'Film is applied one area of glass at a time. Choose the areas for your vehicle — our team confirms the right film for each before the installation.',
      servicesTitle: 'Installation services',
      servicesBody: 'Choose the service that covers the glass you want treated, then a branch and a time.',
      originalTitle: 'Original, recorded, verifiable',
      originalBody: 'Johnson film comes in its original carton, with labels that carry the product code, the batch and the serial number. Every installation is recorded: its QR label and your digital warranty stay with your car.',
      originalVerify: 'Verify a QR code',
      originalLearn: 'How to recognise original film',
      warrantyLink: 'Warranty',
      othersTitle: 'The other Johnson families',
      allFamilies: 'All film families',
      finalTitle: 'See {name} on your vehicle',
      finalBody: 'Choose your vehicle to preview the film glass by glass, or book the installation at a branch near you.',
    },
  },

  viewer: {
    eyebrow: 'Film preview',
    title: 'See the film on your vehicle',
    subtitle: 'Rotate, zoom and choose the glass, the film family and the shade.',
    chooseVehicle: 'First, choose your vehicle',
    changeVehicle: 'Change vehicle',
    service: 'Service',
    glass: 'Glass',
    zones: {
      windshield: 'Front windshield',
      front_left: 'Front left window',
      front_right: 'Front right window',
      rear_left: 'Rear left window',
      rear_right: 'Rear right window',
      rear_quarter_left: 'Rear left quarter glass',
      rear_quarter_right: 'Rear right quarter glass',
      rear_windshield: 'Rear windshield',
      sunroof: 'Sunroof / panoramic roof',
    },
    areas: {
      windshield: 'Windshield',
      frontSides: 'Front side glass',
      rearSides: 'Rear side glass',
      rearGlass: 'Rear glass',
      roof: 'Panoramic roof / sunroof',
    },
    areaNoFilm: 'No film',
    areaNotInService: 'Not part of this service',
    planTitle: 'Your film plan',
    planEmpty: 'Choose the glass to cover.',
    windshieldSeparate: 'The windshield is always chosen separately. What is allowed on it is confirmed with you at the center.',
    family: 'Film family',
    anyFamily: 'No preference yet',
    shade: 'Shade',
    shades: { light: 'Light', medium: 'Medium', dark: 'Dark', deep: 'Very dark' },
    reflectance: 'Reflection',
    reflectances: { low: 'Low', medium: 'Medium', high: 'High' },
    reflectanceApproved: 'From the approved specification.',
    illustrativeGeneric: 'Shade and reflection here are illustrative levels, not product figures.',
    illustrativeFamily: 'Approved figures for this film family are not published yet, so the preview uses illustrative levels.',
    views: 'Views',
    presets: {
      front_three_quarter: 'Front',
      side: 'Side',
      rear_three_quarter: 'Rear',
      top: 'Roof',
    },
    controlsHint: 'Drag to rotate · pinch or scroll to zoom',
    loading3d: 'Loading 3D view…',
    needsCapableDevice: 'The 3D view needs a device with WebGL2. Showing the vehicle’s photo instead.',
    book: 'Book this service',
    previewLink: 'Preview film on this vehicle',
  },

  home: {
    hero: {
      eyebrow: 'Johnson Window Films · Al-Qinaa',
      title: 'Not just the Johnson name.\nVerify the source.',
      subtitle: 'Original film, professional installation and a digital warranty — every installation recorded and traced to your car.',
      primaryCta: 'Choose your vehicle',
      secondaryCta: 'Verify authenticity',
      scrollHint: 'Scroll',
      vehicleCredit: '3D model: {credit}',
      footageCredit: 'Footage: {credit}',
      play: 'Play',
      pause: 'Pause',
      scenesNav: 'Story scenes',
      vehicleAlt: '{vehicle} driving on an open road',
      creditsLink: 'Credits',
    },
    scenes: {
      drive: 'On the road',
      sun: 'Sun & heat',
      film: 'The film',
      protection: 'Original & traced',
      choose: 'Your vehicle',
    },
    sun: {
      label: '02 · Sun & heat',
      title: 'Sunlight is more than light',
      body: 'Sunlight reaching your car carries visible light, infrared heat and ultraviolet — and the glass meets it first. A window film is chosen for how it handles each.',
      visible: { title: 'Visible light', body: 'what you see through the glass.' },
      infrared: { title: 'Infrared', body: 'the part of sunlight you feel as heat.' },
      ultraviolet: { title: 'Ultraviolet', body: 'invisible radiation from the sun.' },
    },
    film: {
      label: '03 · The film',
      title: 'Film, laid glass by glass',
      body: 'Each area of glass is treated on its own — you choose where the film goes and which Johnson film it is.',
      zones: {
        windshield: 'Windshield',
        frontSides: 'Front side glass',
        rearSides: 'Rear side glass',
        rearGlass: 'Rear glass',
        roof: 'Panoramic roof / sunroof',
      },
      note: 'Which areas apply depends on your vehicle and the service you choose.',
      cta: 'Preview film on your vehicle',
    },
    protection: {
      label: '04 · Original & traced',
      title: 'Original film, traced to your car',
      body: 'Every installation is recorded as one chain — from the film product to its kit or roll, the batch, the branch that installed it, the QR label and your digital warranty.',
      verifyCta: 'Verify a QR code',
      learnCta: 'How to recognise original film',
    },
    original: {
      label: 'Original Johnson film',
      title: 'Know the original before it is installed',
      body: 'Original film comes in its Johnson Window Films carton, with side labels that carry the product code, the batch and the serial number. After installation, the QR label on your car confirms the record.',
      learnCta: 'How to recognise original film',
      verifyCta: 'Verify a QR code',
    },
    choose: {
      label: '05 · Your vehicle',
      title: 'Start with your vehicle',
      body: 'Choose the make, model and year. Your vehicle then follows you through the preview and the booking.',
      cta: 'Open the vehicle experience',
    },
    films: {
      label: 'Johnson films',
      title: 'Three Johnson film families',
      body: 'Marathon, InsulatIR and Summit — explore each family, see it on your vehicle and book the installation.',
      detailsCta: 'Details',
      previewCta: 'See it on your vehicle',
      compareCta: 'All film families',
    },
    network: {
      label: 'Branch network',
      title: '{branchesPhrase} in {citiesPhrase}',
      titleFallback: 'Our branches',
      body: 'From Riyadh, Jeddah and Makkah to the Eastern Province, the north, the south and Doha — find the branch nearest to you.',
      cta: 'Open the branch map',
      citiesLabel: 'Cities',
    },
  },

  story: {
    eyebrow: 'Our story',
    title: 'A journey that began in {year}',
    subtitle: 'It began hands-on. Today, decades of field experience become one integrated digital operation.',
    heroCaption: 'Historical photograph — Ahmed Al-Sharif Al-Husseini installing film himself.',
    chaptersLabel: 'The journey',
    chapters: {
      start: {
        year: '1999',
        title: 'Johnson in Saudi Arabia',
        body: 'Work with Johnson Window Films begins in the Kingdom, and the first relationships with the market are built.',
      },
      handsOn: {
        year: 'The beginning',
        title: 'Hands-on, built from the ground up',
        body: 'Ahmed Al-Sharif Al-Husseini works on the cars himself and builds his expertise in the field, one installation at a time.',
      },
      training: {
        year: 'Training',
        title: 'Building installation teams',
        body: 'Technicians are trained and hands-on experience is passed on to organised installation teams.',
      },
      ceramic: {
        year: '2006',
        title: 'Nano-ceramic and professional care',
        body: 'The range of services expands to nano-ceramic protection and professional vehicle care.',
      },
      ppf: {
        year: '2014',
        title: 'Paint protection film',
        body: 'A paint protection film (PPF) line is added.',
      },
      today: {
        year: 'Today',
        title: 'An integrated digital operation',
        body: 'Decades of field experience become one system that traces every order to its roll, its kit, its branch and its warranty.',
      },
    },
    videoLabel: 'In his own words',
    videoTitle: 'A film with Ahmed',
    videoBody: 'A short film to accompany the written story above.',
    craftTitle: 'Experience that cannot be bought — it is built',
    craftBody: 'From installing by hand to a network of branches, trained technicians and a traceability system that links every order to its roll, its kit and its warranty.',
    traceTitle: 'From the roll to your digital warranty',
    traceBody: 'The same care now lives in the platform: each installation is recorded, and its QR label and digital warranty stay with your car.',
    sourcesTitle: 'Sources',
    sourcesBody: 'Dates and milestones as provided by the owner (September 2026).',
    sourcesPhoto: 'The photograph of Ahmed is the owner’s own historical photograph, shown uncropped.',
    ctaBook: 'Book an installation',
    ctaAuthenticity: 'How authenticity works',
  },

  films: {
    eyebrow: 'Johnson films',
    title: 'Johnson film families',
    subtitle: 'Marathon, InsulatIR and Summit. Open a family for its details, or see it on your vehicle before you book.',
    metrics: {
      vlt: 'Visible light transmission (VLT)',
      tser: 'Total solar energy rejected (TSER)',
      ir: 'Infrared (IR / SIRR)',
      uv: 'UV rejection',
      glare: 'Glare reduction',
    },
    explainTitle: 'What the numbers mean',
    explainBody: 'Window film is described by separate measurements. Each one is shown on its own — never merged into one number.',
    explain: {
      vlt: 'How much visible light passes through the glass with the film. A lower value means a darker film.',
      tser: 'How much of the sun’s total energy — light, heat and ultraviolet together — is kept out.',
      ir: 'How much infrared, the part of sunlight you feel as heat, is kept out.',
      uv: 'How much ultraviolet radiation is kept out.',
      glare: 'How much the glare of bright light is reduced.',
    },
    approvedNote: 'Figures from the approved specification sheets.',
    compareTitle: 'Side by side',
    chooseFor: 'See it on your vehicle',
    details: 'Details',
    range: '{from}–{to}%',
    percent: '{value}%',
  },

  privacy: {
    eyebrow: 'Privacy',
    title: 'Privacy policy',
    intro: 'At Johnson’s, we take the privacy of all users into account, and we are committed to protecting all information collected through the website, such as:',
    items: {
      registration: 'all personal information collected during the registration procedure;',
      orders: 'all purchase-order details and purchase records;',
      delivery: 'all delivery information and general contact details.',
    },
    retain: 'We would like to reassure you that we retain these details to allow us to execute your orders and meet your requirements in general. We also take all necessary steps to protect your personal information against unauthorised access, use, disclosure or damage.',
    disclose: 'We will disclose your personal information as reasonably necessary to fulfil your request or to fulfil an order or purchase, including but not limited to providing your address to a shipping or delivery company, or sending your information to a banking institution or to our payment gateway provider for approval of your transaction. However, please note that if we are asked by any regulator or government authority investigating suspicious activity to provide your personal details or user information, we will be entitled to do so.',
    marketing: 'We may also use your personal information to send you our newsletters and details of special offers, or promotional or product offers that may interest you.',
    source: 'Text supplied by the owner (jwfarabia.com privacy policy, September 2026), with its sentence order restored. Pending legal review for this platform.',
  },

  credits: {
    eyebrow: 'Credits',
    title: 'Credits and licences',
    intro: 'Third-party media shown on this site, with its owner, source, licence and the changes we made to it.',
    modelTitle: '{name} — 3D model',
    owner: 'Owner',
    source: 'Source',
    licence: 'Licence',
    status: 'Status',
    temporary: 'Temporary staging vehicle while Range Rover, the requested production vehicle, awaits a licensed, approved asset.',
    use: 'Where it appears',
    useBody: 'Shown on the staging home page (not production) as a temporary stand-in, replaced automatically the moment an approved Range Rover asset is registered.',
    changes: 'Changes made',
    trademark: 'BMW and X5 are trademarks of BMW AG, used here only to identify the vehicle model shown. BMW does not sponsor, endorse or partner with Al-Qinaa.',
    licenceNote: 'CC BY 4.0 allows the model to be shared and adapted, with credit, a link to the licence and an indication of the changes made.',
  },

  authenticity: {
    eyebrow: 'Authenticity',
    title: 'How to recognise original Johnson film',
    subtitle: 'What to look for before and after your installation — and exactly what you can check yourself today.',
    stepsTitle: 'Ten things to check',
    stepsIntro: 'From the sealed carton to your digital warranty: each item below is part of the record of a genuine installation.',
    steps: {
      carton: { title: 'Original carton', body: 'The film comes out of an original Johnson Window Films carton. Ask to see it before the installation starts.' },
      innerBox: { title: 'Internal protective box', body: 'Inside the carton, the roll or kit sits in its internal protective box.' },
      roll: { title: 'Film and roll packaging', body: 'The roll or pre-cut kit stays in its original packaging until it is opened for your car.' },
      sideLabels: { title: 'Side labels', body: 'The carton and box carry the original labels on their sides.' },
      johnsonCode: { title: 'Johnson code', body: 'The product code on the label identifies the exact film.' },
      batch: { title: 'Batch number', body: 'The batch number ties the package to its production lot.' },
      serial: { title: 'Serial or kit number', body: 'Each roll or kit carries its own serial or kit number.' },
      qr: { title: 'QR label', body: 'Your installation receives a QR label — the one code anyone can check online.' },
      center: { title: 'Installation branch', body: 'The branch that installed your film is recorded with the installation.' },
      warranty: { title: 'Digital warranty', body: 'Your warranty is recorded digitally and stays in your account.' },
    },
    publicTitle: 'What you can check online today',
    publicBody: 'The QR code on your installation label is the one code anyone can check online. Kit numbers, film codes, batch and serial numbers and order numbers stay on your private installation record.',
    publicCheckable: 'Checkable online',
    privateRecord: 'On your private record',
    photosTitle: 'Original packaging',
    photosBody: 'Photographs from our branches of the original product components.',
    chainTitle: 'Your installation record, code by code',
    chainIntro: 'These are the codes the platform keeps for an installation. Only the QR code can be checked publicly; the rest stay private to you and the installing center.',
    items: {
      film: { name: 'Film code', what: 'The code of the Johnson film product that was installed.' },
      kit: { name: 'Kit or roll number', what: 'The package (kit or roll) the film came from.' },
      batch: { name: 'Batch and serial', what: 'The batch and serial number of that package, as received.' },
      center: { name: 'Installation center', what: 'The center that installed the film.', where: 'In your account, on your warranty record.' },
      order: { name: 'Order number', what: 'The number of your order with us.' },
      qr: { name: 'QR code', what: 'A unique code on the QR label of your installation.', where: 'Check it on the Verify page — no personal data is shown.' },
      warranty: { name: 'Digital warranty', what: 'Your warranty number and status, linked to the installation.', where: 'In your account, under My warranty.' },
    },
    recordedWhere: 'Recorded with your installation.',
    orderPrivate: 'Never searchable publicly, to protect your privacy.',
    liveVerify: 'Verify a QR code',
    liveAccount: 'Open my warranty',
    verifyOtherCodes: 'Have a different code? See what each code on your record means.',
  },

  centers: {
    title: 'Our branches',
    subtitle: '{branchesPhrase} in {citiesPhrase}. Find the nearest one, get directions or call ahead.',
    subtitleFallback: 'Find the nearest branch, get directions or call ahead.',
    filterLabel: 'City',
    allCities: 'All cities',
    empty: 'No branches to show yet.',
    addressLabel: 'Address',
    phoneLabel: 'Phone',
    call: 'Call',
    servicesLabel: 'Services',
    availabilityLabel: 'Online booking',
    nextSlot: 'Next available: {time}',
    noSlotSoon: 'No online times in the next two weeks.',
    bookingNotOpen: 'Not bookable online yet — call the branch or contact us.',
    book: 'Book here',
    directions: 'Directions',
    showOnMap: 'Show on map',
    nearMe: 'Nearest to me',
    locating: 'Finding your location…',
    locationDenied: 'Location access was not allowed. Choose a city instead.',
    locationUnavailable: 'Your location could not be found right now. Choose a city instead.',
    distanceKm: '{km} km away (straight line)',
    youAreHere: 'Your location',
    nearestFound: 'Nearest branch: {name}',
    mapLabel: 'Map of Al-Qinaa branches',
    mapUnavailable: 'The interactive map is not available here. Every branch is listed below.',
    mapKeyHint: 'Interactive map not configured: add VITE_MAPTILER_KEY (docs/phase-3-staging-apply.md).',
    sourceNote: 'Locations from the official branch list and each branch’s public map listing, checked {date}.',
    listingNameDiffers: 'Listed on the map under a different trading name.',
    noPublicListing: 'No public map listing — location from the official branch list.',
    reset: 'All branches',
    listCount: plural({ one: '{count} branch', other: '{count} branches' }),
    resultsFor: plural({ one: '{count} branch in {city}', other: '{count} branches in {city}' }),
  },

  booking: {
    title: 'Book a visit',
    subtitle: 'Choose a service, a center and a time. It takes about two minutes.',
    steps: {
      service: 'Service',
      schedule: 'Center & time',
      account: 'Account',
      vehicle: 'Vehicle',
      review: 'Review',
    },
    chooseService: 'Choose a service',
    chooseCenter: 'Center',
    chooseDate: 'Date',
    chooseTime: 'Available times',
    noTimes: 'No available times on this date. Try another day.',
    noTimesShort: 'no times',
    nextAvailable: 'Next available: {date}',
    noAvailability: 'No available times at this center in the next two weeks. Try another center or contact us.',
    signInToContinue: 'Sign in or create a free account to confirm your booking. Your choices are saved.',
    chooseVehicle: 'Which vehicle?',
    addVehicleInline: 'Add a new vehicle',
    myVehicles: 'My vehicles',
    noVehiclesYet: 'You have not added a vehicle yet.',
    continueWithoutVehicle: 'Continue without a vehicle',
    vehicleNotRequired: 'This service does not require a vehicle.',
    notes: 'Notes for the center',
    review: 'Review your booking',
    confirm: 'Confirm booking',
    confirming: 'Confirming…',
    successTitle: 'Your booking request is in',
    successBody: 'Booking {number} is awaiting confirmation from the center. We’ll let you know as soon as it’s confirmed.',
    viewBookings: 'View my bookings',
    unavailable: 'Online booking is paused for the moment. Please contact us and we’ll book you in.',
    centersNotBookable: 'Our branches are not bookable online yet — their hours are still being confirmed. Call your nearest branch and they will book you in.',
    callBranch: 'Call {name}',
    allBranches: 'Find your nearest branch',
    stagingTestTitle: 'Staging: try the full booking flow',
    stagingTestBody: 'Real branches open online once their hours are verified. Use the labelled test center — it is not a real location and never appears on the live site.',
    stagingTestCta: 'Use the staging test center',
    slotTaken: 'Someone just booked that time. Please pick another.',
    chooseAnotherTime: 'Choose another time',
    back: 'Back',
    continueCta: 'Continue',
    change: 'Change',
    dayParts: {
      morning: 'Morning',
      afternoon: 'Afternoon',
      evening: 'Evening',
    },
    interests: {
      title: 'Your interest',
      note: 'The center will confirm the final film with you.',
      remove: 'Remove',
    },
    summary: {
      service: 'Service',
      center: 'Center',
      time: 'Date & time',
      vehicle: 'Vehicle',
      noVehicle: 'No vehicle selected',
    },
  },

  bookingStatus: {
    pending: 'Pending',
    confirmed: 'Confirmed',
    rescheduled: 'Rescheduled',
    cancelled: 'Cancelled',
    completed: 'Completed',
  },

  verify: {
    title: 'Verify your installation',
    body: 'Enter the code from your QR label to see your installation and warranty record.',
    code: 'QR code',
    submit: 'Verify',
    valid: 'Record found',
    invalid: 'We couldn’t find a record for this code. Check it and try again, or contact us for help.',
    product: 'Product',
    family: 'Product family',
    warrantyStatus: 'Warranty status',
    warrantyPeriod: 'Warranty period',
    installedOn: 'Installed on',
    help: 'The code is printed under the QR label on your glass. You can also scan the label with your phone camera.',
    accountCta: 'View my warranty',
    contactCta: 'Contact us',
    warrantyStates: {
      pending_activation: 'Pending activation',
      active: 'Active',
      expired: 'Expired',
      void: 'Void',
    },
  },

  track: {
    title: 'Track your shipment',
    body: 'Enter the tracking code from your message to see the latest status.',
    code: 'Tracking code',
    submit: 'Track',
    notFound: 'We couldn’t find a shipment with this code. Check it and try again.',
    status: 'Status',
    timeline: 'Progress',
    expected: 'Expected arrival: {date}',
    deliveredOn: 'Delivered on {date}',
    myShipments: 'My shipments',
    help: 'Your tracking code is in the message we sent when your shipment was booked. Need help? Contact us.',
    statuses: {
      created: 'Booked',
      scheduled: 'Pickup scheduled',
      picked_up: 'Collected',
      in_transit: 'On the way',
      arrived: 'Arrived',
      delivered: 'Delivered',
      cancelled: 'Cancelled',
      exception: 'Needs attention — our team will contact you',
    },
  },

  warrantyPage: {
    howTitle: 'How it works',
    steps: [
      { title: 'Installation at our center', body: 'Your chosen film is fitted by our team and the installation is recorded.' },
      { title: 'Your QR label', body: 'A QR label links your glass to its installation record.' },
      { title: 'Warranty in your account', body: 'Your warranty record is saved to your account — check it anytime.' },
    ],
    title: 'Digital warranty',
    body: 'When your installation is complete, your warranty record is created and saved to your account. Look it up anytime with your QR code. Warranty terms follow the official warranty policy for your film.',
    verifyCta: 'Verify with a QR code',
    accountCta: 'Open my account',
  },

  contactPage: {
    optionsTitle: 'How can we help?',
    options: {
      branches: { title: 'Call a branch', body: 'Every branch’s phone number and directions are on the branches page.', cta: 'Find a branch' },
      book: { title: 'Book a visit', body: 'Choose a service, a center and a time online.', cta: 'Book now' },
      bookings: { title: 'Your bookings', body: 'View, or cancel, an upcoming visit.', cta: 'My bookings' },
      verify: { title: 'Check an installation', body: 'Look up your installation and warranty record.', cta: 'Verify' },
      track: { title: 'Track a shipment', body: 'See the latest status of your shipment.', cta: 'Track' },
    },
    title: 'We’re here to help',
    body: 'Questions about films, bookings or your warranty? Reach our team.',
    channelsPending: 'Central contact channels (phone, email, WhatsApp) are published once the business confirms them.',
  },

  errors: {
    notFoundTitle: 'Page not found',
    notFoundBody: 'The page you are looking for does not exist or has moved.',
    forbiddenTitle: 'Access denied',
    forbiddenBody: 'Your account does not have access to this area.',
    goHome: 'Go to home',
    goPortal: 'Go to my portal',
    routeErrorTitle: 'Something went wrong',
    routeErrorBody: 'An unexpected error occurred while loading this page.',
    reload: 'Reload page',
  },
} as const;

type Widen<T> = T extends string
  ? string
  : T extends readonly (infer U)[]
    ? readonly Widen<U>[]
    : T extends object
      ? { readonly [K in keyof T]: Widen<T[K]> }
      : T;

export type Messages = Widen<typeof en>;
